<script lang="ts">
  /**
   * SharedMorningCard — the Club "this morning" card. Renders EITHER the
   * open-water shape (shared water-temp fact, the viewer's native route) OR the
   * pool shape (each swimmer's own condensed set strip), branched on the
   * session's locationType — a ClubSession is homogeneous by construction
   * (clubSessions.ts buckets by day+location before clustering), so this is a
   * single branch, not a per-row decision. Prop-driven and self-contained
   * (showcase-no-fork): all logic is in buildSharedMorning; this only binds the
   * view-model to markup, plus the app's OWN SetGroupRibbon for the pool strip.
   *
   * Pool strips render non-tappable (aria-hidden, no SetDetailSheet wiring) —
   * see roadmap for why: SetDetailSheet's "Fix a stroke" affordance writes
   * through a SINGLE global currentLabels/commitLabelChange pair meant for the
   * viewer's own currently-open swim, so pointing it at a roster row's derived
   * workout (a companion's swim, or another one of the viewer's own swims) would
   * either let the viewer edit data that isn't theirs or clobber whatever the
   * rest of the app currently has open. Not cheap — logged, not wired.
   */
  import type { SharedMorning } from "../lib/sharedMorning";
  import OpenWaterRouteCard from "./OpenWaterRouteCard.svelte";
  import SetGroupRibbon from "./SetGroupRibbon.svelte";
  import { fmtMeters, formatDuration } from "../lib/format";

  interface Props {
    morning: SharedMorning;
    /** Whether to offer the native route tap. FALSE in Plan 3 (fixtures carry no
     *  real HealthKit route — a tap would query a workout that doesn't exist).
     *  Plan 4 passes true once routeWorkoutUUID points at a real synced swim.
     *  No-op for a pool session (no route to show). */
    showRoute?: boolean;
    /** Item 2a: with 2+ clubmates on one morning, the roster IS the swimmer
     *  picker — tapping a clubmate's row compares You vs them. Only rows the
     *  parent lists here become tappable (clubmates with a swim); the viewer's
     *  own row and tap-attendees never do. */
    pickableMemberIds?: string[];
    /** The clubmate currently compared (highlighted with a "compared" tag). */
    selectedMemberId?: string | null;
    onPickMember?: (memberId: string) => void;
    /** Pool sessions: render each swimmer's condensed set strip. ClubTab turns
     *  this off in the shared-morning layout, where SETS TODAY owns the sets. */
    showSetStrips?: boolean;
  }
  let {
    morning,
    showRoute = true,
    pickableMemberIds = [],
    selectedMemberId = null,
    onPickMember,
    showSetStrips = true,
  }: Props = $props();

  // Only offer the tap when there is a real choice to make (2+ pickable rows).
  let pickable = $derived(pickableMemberIds.length >= 2 && onPickMember !== undefined);

  // Session-level branch (all entries share one locationType). "unknown" (the
  // defensive third bucket) degrades to the chrome-free branch — there is no
  // GPS route, water temp, OR pool-ness to claim either way, and the set strip
  // itself degrades cleanly per-row when a firehose doesn't resolve.
  let isOpenWaterSession = $derived(morning.session.locationType === "openWater");
  // VP common-sense panel fix (club-pool-swims round, 2026-07-23): 3/4
  // panelists read the pool card as half-loaded because the OW marker slot
  // — "Open water · 73°F" — went completely blank for a pool session instead
  // of naming the session type. Pool gets the SAME marker position/style,
  // text only (no temp fact exists for pool).
  let isPoolSession = $derived(morning.session.locationType === "pool");
</script>

<!-- One roster line, rendered inside a <button> (tappable clubmate) or a <div>. -->
{#snippet rowTop(r: SharedMorning["roster"][number])}
  <span class="roster-name">
    {r.label}{#if r.isViewer && r.label !== "You"}<span class="you-tag">you</span>{/if}
    {#if pickable && r.memberId === selectedMemberId}<span class="you-tag compared-tag">compared</span>{/if}
  </span>
  {#if r.distanceMeters !== null}
    <span class="roster-dist">{fmtMeters(r.distanceMeters)}{#if r.durationSeconds !== null}{" · "}{formatDuration(r.durationSeconds)}{/if}</span>
  {:else}
    <span class="roster-attended">was there</span>
  {/if}
{/snippet}

<div class="morning-card">
  {#if isOpenWaterSession}
    <p class="ow-marker">
      <span class="ow-dot" aria-hidden="true"></span>
      <span class="ow-text">Open water</span>
      {#if morning.waterTempLabel}<span class="ow-temp">· {morning.waterTempLabel}</span>{/if}
    </p>

    {#if showRoute}
      <OpenWaterRouteCard workoutUUID={morning.routeWorkoutUUID} openWater={true} />
    {/if}
  {:else if isPoolSession}
    <p class="ow-marker">
      <span class="ow-dot pool-dot" aria-hidden="true"></span>
      <span class="ow-text">Pool</span>
    </p>
  {/if}

  <div class="roster">
    <p class="roster-caption" id="roster-caption">Who was there</p>
    <ul class="roster-list" aria-labelledby="roster-caption">
      {#each morning.roster as r (r.swimUUID)}
        <li class="roster-row">
          {#if pickable && pickableMemberIds.includes(r.memberId)}
            <button
              class="roster-row-top roster-row-btn"
              type="button"
              aria-pressed={r.memberId === selectedMemberId}
              onclick={() => onPickMember?.(r.memberId)}
            >
              {@render rowTop(r)}
            </button>
          {:else}
            <div class="roster-row-top">
              {@render rowTop(r)}
            </div>
          {/if}
          {#if r.setGroups && r.setGroups.length === 0}
            <p class="no-lengths" data-testid="club-no-lengths">No lengths recorded</p>
          {:else if showSetStrips && r.setGroups && r.setGroups.length > 0}
            <div class="set-strip">
              {#each r.setGroups as sg (sg.index)}
                <SetGroupRibbon setGroup={sg} label={true} />
              {/each}
            </div>
          {/if}
        </li>
      {/each}
    </ul>
  </div>
</div>

<style>
  .morning-card {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  /* OW marker — same calm identity line as SwimHeroCard (metadata tier). */
  .ow-marker {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: 0;
    font-size: 15px;
    line-height: 1.3;
  }
  .ow-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    background: var(--accent-secondary);
  }
  /* Pool marker (VP panel fix): a distinct dot color from open-water's so the
     two badges read as different session types at a glance, not the same
     chrome with different words. */
  .pool-dot {
    background: var(--accent-primary);
  }
  .ow-text {
    color: #c5c6cc;
    font-weight: 600;
  }
  .ow-temp {
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }

  .roster {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .roster-caption {
    font-size: 12px;
    color: #abacb4;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .roster-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .roster-row {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-height: 32px;
    padding: var(--space-2) 0;
    border-bottom: 1px solid var(--divider);
  }
  .roster-row-top {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--space-3);
  }
  /* Tappable clubmate row (item 2a, 3+ swimmers): same layout, 44px target,
     no button chrome — the "compared" tag is the only state marker. */
  .roster-row-btn {
    width: 100%;
    min-height: 44px;
    padding: 0;
    background: transparent;
    border: none;
    font: inherit;
    text-align: left;
    cursor: pointer;
  }
  .compared-tag {
    color: var(--text-muted);
  }
  /* Condensed set strip (pool sessions only) — the app's OWN SetGroupRibbon
     chip rows, one per set, in swim order. Non-tappable glance, not the edit
     surface (see component doc for why SetDetailSheet isn't wired here).
     VP panel fix (club-pool-swims round): row-wrap let separate sets' chips
     flow onto the same visual line with no boundary between them — a wall of
     chips read as "confetti" and a real single-length set (small, dimmer
     chip-lone) looked like a clipped/broken chip floating mid-row. Stacking
     column-wise gives every set its OWN block, and each block now carries its
     own label (SetGroupRibbon's `label` prop) — a set boundary is visible
     even without a rest-gap divider.
     VP panel round 2 (2026-07-23): a thin top hairline + a touch of padding
     so the roster line ("Scott · 350m") and the first set label ("Warmup ·
     100m") don't run together — without it, "The Rockalita" briefly read as
     a third attendee's name before the chips registered. Condensed on
     purpose: a divider line, not a labeled "Sets" section header. */
  .set-strip {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    border-top: 1px solid var(--divider);
    padding-top: var(--space-2);
  }
  .no-lengths {
    margin: 0;
    font-size: 14px;
    color: var(--text-muted);
  }
  .roster-name {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
  }
  .you-tag {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--accent-primary);
  }
  .roster-dist {
    font-size: 15px;
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }
  .roster-attended {
    font-size: 14px;
    font-style: italic;
    color: var(--text-muted);
  }
</style>
